import React, { useEffect, useState } from 'react';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { auth } from '../services/firebase';
import AuthLayout from './AuthLayout';
import { useNavigate, useLocation, Link, useSearchParams } from 'react-router-dom';

const ResetPasswordPage: React.FC = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmError, setConfirmError] = useState('');
  const [emailError, setEmailError] = useState('');

  const [searchParams] = useSearchParams();
  const [token, setToken] = useState<string>('');
  const queryValue = searchParams.get('token');

  useEffect(() => {
      if (queryValue) {
        setToken(queryValue);
        // You can add logic here to verify the token with your backend
        console.log('Verification token:', queryValue);
      } else {
        console.error('No verification token found in URL');
      }
  }, [queryValue]);

  const validatePassword = (password: string) => {
    const regex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;
    return regex.test(password);
  };

  const validateEmail = (email: string) =>
    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);

  const computeConfirmError = (pwd: string, conf: string): string => {
    if (!conf) return '';
    if (conf.length < pwd.length) return ''; // still typing: no error yet
    if (conf !== pwd) return 'Passwords do not match';
    return '';
  };

  const handlePasswordChange = (value: string) => {
    setNewPassword(value);

    if (!validatePassword(value)) {
      setPasswordError(
        'Password must be at least 8 characters, include uppercase, lowercase, a number, and a special character.'
      );
    } else {
      setPasswordError('');
    }
    setConfirmError(computeConfirmError(value, confirmPassword));
  };

  const handleConfirmChange = (value: string) => {
    setConfirmPassword(value);
    setConfirmError(computeConfirmError(newPassword, value));
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (!validateEmail(value)) {
      setEmailError('Please enter a valid email.');
      return;
    } else {
      setEmailError('');
    }
    
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validatePassword(newPassword)) {
      setError(
        'Password must be at least 8 characters, include uppercase, lowercase, a number, and a special character.'
      );
      return;
    }

    if (newPassword !== confirmPassword) {
      setConfirmError('Passwords do not match');
      return;
    }

    if (!validateEmail(email) || !email.trim()) {
      setEmailError('Please enter a valid email.');
      return;
    }

    if(!token) {
      setError('No reset code found. Please try resetting your password again.');
      return;
    }

    setLoading(true);

    try {
      const res = await fetch('/.netlify/functions/reset-user-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, token, newPassword }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to reset password');
        setError('Failed to reset password. Please try again.');
      } else {
        setSuccess(true);
        setError('');
        setNewPassword('');
        setConfirmPassword('');
        setEmail('');
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000); // Redirect after 2 seconds
      }
    } catch (err) {
      setError('Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isFormValid =
    newPassword.length > 0 &&
    confirmPassword.length > 0 &&
    validatePassword(newPassword) &&
    newPassword === confirmPassword &&
    !passwordError &&
    !confirmError;

  return (
    <AuthLayout title="Reset Password" showBackButton={false}>

      
        <form onSubmit={handleSubmit}>
          
          <div className="formGroup">
            <input
              type="email"
              placeholder="Enter your registered email"
              value={email}
              onChange={(e) => handleEmailChange(e.target.value)}
              required
              autoFocus={true}
            />
            {emailError && (
              <div className="errorText">{emailError}</div>
            )}
          </div>

          <div className="formGroup">
            <input
              type="password"
              placeholder="New Password"
              value={newPassword}
              onChange={(e) => handlePasswordChange(e.target.value)}
              required
            />
            {passwordError && (
              <div className="errorText">{passwordError}</div>
            )}
          </div>

          <div className="formGroup">
            <input
              type="password"
              placeholder="Confirm New Password"
              value={confirmPassword}
              onChange={(e) => handleConfirmChange(e.target.value)}
              required
            />
            {confirmError && (
              <div className="errorText">{confirmError}</div>
            )}
          </div>

          <button type="submit" className="btnStyle" disabled={!isFormValid}>
            {
            loading ? 'Reseting Your Password...' : 'Reset Password'
            }
          </button>
        </form>

        {error && <p className="text-red-600 mt-4">{error}</p>}
        {success && (
          <p className="text-green-600 mt-4">
            Password reset successfully! Redirecting to login...
          </p>
        )}
    </AuthLayout>
  );
};

export default ResetPasswordPage;
